import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import BuildReadyStrip, { loadBuildReady } from './BuildReadyStrip.jsx'
import { createPortal } from 'react-dom'
import { wf } from '../outline/state.js'
import { ctx } from '../outline/ctx.js'
import { getFirst } from '../outline/utils.js'
import {
  seedFromMilestones, saveScheduleDates, writeBackDates, fetchCurrentAirtableDates,
  saveDependencies, writeBackPredecessors, getProjectTimeline,
} from '../services/schedule.js'
import { acquireLock, heartbeatLock, releaseLock } from '../services/locks.js'
import { getProjectMilestones } from '../services/milestones.js'
import { getProjectPhases } from '../services/phases.js'
import { getProjectKeyDates } from '../services/keyDates.js'
import { getProjectFeeders } from '../services/feeders.js'
import { crewForMilestone, crewLabel, ensureCrewIndex } from '../outline/crew.js'
import {
  computeCascade, parseDate, formatDate, addDays, dayDiff, nextWorkday,
} from '../lib/cascade.js'
import { todayUTC, fmtShort, isLocked, buildWindow, buildPhaseLayout } from '../lib/ganttLayout.js'
import GanttTimeline, {
  ZOOMS, PRIMARY, TEXT, BG, CRIT, PROG, HOVER_BG, ROW_HILITE,
} from './gantt/GanttTimeline.jsx'
import { confirmDialog } from './Dialog.jsx'
import { useScrollLock } from '../hooks/useScrollLock.js'

const ROW_H        = 26
const LABEL_W      = 340
const HEARTBEAT_MS = 4 * 60 * 1000  // 4 min (lock TTL is 30 min)

// Airtable field names for updating the local milestone cache after write-back
const F_EST = 'Earliest Start (EST)'
const F_LFT = 'Latest Finish (LFT)'
const F_DUR = 'Duration'

// "Locked" for DRAG/manual-edit purposes: only an explicit Date Pinned lock blocks the user's own
// hand — Fixed/Scheduled behavior alone should not (see ganttLayout.js#isLocked for the display-only
// version), since those dates are frequently set/moved by the team the moment an outside party
// (e.g. a Planning agency) actually schedules them.
// A Fixed/Scheduled type stays draggable even when locked: its lock only means "this is the real date
// we were given", and when the agency reschedules the hearing someone drags it to the new one (which
// keeps it locked — applyChange).
function isDragLocked(row) {
  if (row.milestone_behavior === 'Fixed' || row.milestone_behavior === 'Scheduled') return false
  return !!row.date_pinned
}

// Posted? Airtable masters "is it posted" and the outline already holds this project's records, so
// read that first; the schedule mirror's posted_date is the fallback (it can lag a day).
function postedDateOf(row) {
  const rec = (ctx.state?.milestones || []).find(m => m.id === row.airtable_id)
  const at = rec && rec.fields && rec.fields['Posted Date']
  const v = at || row.posted_date || null
  return v ? String(v).slice(0, 10) : null
}

// The two board-facing fever-chart numbers (docs/RULES_OF_FLOW.md §4's reference figure — a Project
// Status Chart plotting % buffer consumed against % chain complete), read compactly in the Gantt
// toolbar. Full two-diagonal banding belongs to the fever chart itself, wherever that gets built
// (the Flight Board is the named destination, item 4 of the Gantt-fixes hand-off); this chip uses a
// simple threshold on buffer consumption alone as a fast visual cue while looking at one project's
// own schedule, not a substitute for the real chart.
function TimelineChip({ timeline }) {
  if (!timeline || !timeline.buffer_days) return null
  const chainPct = Math.round((timeline.chain_complete_pct || 0) * 100)
  const bufferPct = Math.min(100, Math.round((timeline.buffer_consumed_days / timeline.buffer_days) * 100))
  const color = bufferPct >= 66 ? CRIT : bufferPct >= 33 ? '#c9a227' : PROG
  return (
    <span title={`Chain ${chainPct}% complete · Buffer ${timeline.buffer_consumed_days}d of ${Math.round(timeline.buffer_days)}d consumed (${bufferPct}%)`}
      style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, padding: '3px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: TEXT, background: 'var(--aj-bg-elevated)', display: 'flex', alignItems: 'center', gap: 6 }}>
      <span>Chain {chainPct}%</span>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block' }} />
      <span>Buffer {bufferPct}%</span>
    </span>
  )
}

export default function GanttModal() {
  const [open, setOpen]       = useState(false)
  const [projectId, setProjectId] = useState(null)
  const [rows, setRows]       = useState([])      // working copy
  const [deps, setDeps]       = useState([])
  // Calendar flags (docs/KEY_DATES.md) — not milestones, not scheduled; fetched alongside the
  // schedule and rendered as diamonds by GanttTimeline. Never blocks the schedule load.
  const [keyDates, setKeyDates] = useState([])
  // Build Ready for this project (the strip's shared read): the target column + buffer row on the chart.
  const [buildReady, setBuildReady] = useState(null)
  // THIS project's own milestone records and phases. Never ctx.state.milestones / ctx.state.phases:
  // those belong to whatever project the outline shows behind the modal, which is a different one
  // (or none) when the Gantt opens from the line-up, the Tower or the Flight Board — every phase
  // row then read "PHASE" and the progress fill was blank (Matt, 2026-09-24).
  const [msRecords, setMsRecords] = useState([])
  const [phaseList, setPhaseList] = useState([])
  // Per-deliverable-type effort estimate, keyed by deliverable-type airtable id (api/schedule.js
  // `fetchEffort`). Display only — the float math still reads each milestone's own duration_days.
  const [effort, setEffort]   = useState({})
  // Feeders on this project (build step 5): milestone id -> { owedBy, state, expectedOn, ... }.
  // Display only; the dates themselves are already on the rows (api/_feederSchedule.js sets them).
  const [feeders, setFeeders] = useState({})
  // The buffered project timeline (chain + project buffer, sheets 5+6 of docs/RULES_OF_FLOW.md) —
  // fetched alongside the schedule, never blocking it. null = not yet loaded / not applicable.
  const [timeline, setTimeline] = useState(null)
  // Opened from an embedded frame (the line-up's Schedule window sits at 750, or 1000001 over the
  // On Deck lockout): lift above it. Dialogs (1000100) still land on top.
  const [onTop, setOnTop]     = useState(false)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving]   = useState(false)
  const [error, setError]     = useState(null)
  const [readOnly, setReadOnly] = useState(false)
  const [lockHolder, setLockHolder] = useState(null)
  const [zoom, setZoom]       = useState('days')
  const [collapsedPhases, setCollapsedPhases] = useState(() => new Set())
  const [selectedId, setSelectedId] = useState(null)   // bar selected for keyboard nudge
  // Work-band hover (tooltip + day highlight) and dependency-connector hover both live inside
  // GanttTimeline now (self-contained, presentational) — see gantt/GanttTimeline.jsx.
  const [dragTip, setDragTip] = useState(null)          // { x, y, est, lft, mode } — live dates while dragging/resizing a bar
  const [hoverRowId, setHoverRowId] = useState(null)    // row-hover highlight (left label + timeline band) — shared with GanttTimeline via hoveredRowId/onHoverRow
  const [menuFor, setMenuFor] = useState(null)          // { id, x, y } — open action (wrench) menu
  const [linking, setLinking] = useState(null)          // { fromId, x1, y1, x2, y2 } — connector drag in progress
  const [creating, setCreating] = useState(null)        // { rowId, x1, x2 } — drawing a date range on an undated row
  const [past, setPast]       = useState([])            // undo stack — [{rows, deps}] max 50
  const [future, setFuture]   = useState([])            // redo stack — [{rows, deps}]

  const depsSnapRef  = useRef(new Set())                // original dep edges 'ms|pred' at open (dirty check)
  const contentRef   = useRef(null)                     // timeline content area (for connector hit-testing)
  const createRef    = useRef(null)                     // create-drag state: { rowId, rect, x0, windowStart }
  const backdropRef  = useRef(null)                     // overlay div — used for non-passive wheel capture
  const headH = 42                                      // two header bands (month/week on top, cells below)

  const savedRef     = useRef(false)                    // this session saved (read by an embedded frame on close)
  const snapshotRef  = useRef(new Map())   // airtable_id -> { est, lft, duration } at open
  const heartbeatRef = useRef(null)
  const dragRef      = useRef(null)

  // Reference-counted (hooks/useScrollLock.js) — this modal can open ON TOP of a workspace
  // (Pulse's Flight Tracker) that already holds its own lock; a naive raw
  // document.body.style.overflow set/restore here would clear THAT lock the moment this modal
  // closes, even though the workspace underneath is still open and needs it.
  useScrollLock(open)
  const scrollRef    = useRef(null)
  const didCenterRef = useRef(false)

  const PX = ZOOMS[zoom].px

  // Crew labels arrive asynchronously (outline/crew.js resolves the index once, then fires
  // `outline-render`). Bumping this re-runs infoMap so a bar that drew with no crew picks one up
  // the moment the index lands, and again after any crew change on the Flight Board.
  const [crewTick, setCrewTick] = useState(0)
  useEffect(() => {
    if (!open) return
    ensureCrewIndex()
    const onRender = () => setCrewTick(t => t + 1)
    window.addEventListener('outline-render', onRender)
    return () => window.removeEventListener('outline-render', onRender)
  }, [open])

  // Per-milestone display extras (progress %, crew) pulled from the loaded Airtable records.
  // ctx.state.milestones is stable for the modal session; keyed by airtable_id (= ms.id).
  // The crew is NOT an Airtable field: RASCI was retired 2026-09-02 and crews live in Supabase
  // (docs/FLOW_RECONCILIATION_PLAN.md §0), so it is read from the crew index. A queued milestone
  // has no crew by design — the bar simply carries no name.
  const infoMap = useMemo(() => {
    const m = new Map()
    const byId = new Map(msRecords.map(x => [x.id, x]))
    rows.forEach(r => {
      const f = byId.get(r.airtable_id)?.fields || {}
      let prog = typeof f['Progress'] === 'number' ? f['Progress']
        : (typeof f['Percent Complete Checklist Items'] === 'number' ? f['Percent Complete Checklist Items'] / 100 : 0)
      prog = Math.max(0, Math.min(1, prog || 0))
      const assignee = crewLabel(crewForMilestone(r.airtable_id))
      // Project Phase text — fallback for grouping when the Phase link (phase_id) is missing.
      const projectPhase = getFirst(f['Project Phase']) || ''
      m.set(r.airtable_id, { progress: prog, assignee, projectPhase })
    })
    return m
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, crewTick, msRecords])

  // ── Open / close lifecycle ──────────────────────────────────────────────────
  useEffect(() => {
    function onOpen(e) {
      const pid = e.detail?.projectId || wf.zoomProjectId
      if (!pid) return
      // Inside an embedded copy of the app (the line-up's Schedule window, a split pane) the Gantt
      // would open squeezed into that small frame. Hand it to the main window instead, at full size,
      // and reload this frame after a save so it shows the new dates (Matt, 2026-09-24).
      if (!e.detail?.fromFrame && window.parent !== window) {
        let parent = null
        try { if (window.parent.__ajGanttReady) parent = window.parent } catch (_) { /* other origin */ }
        if (parent) {
          const onClosed = (ev) => { parent.removeEventListener('aj-gantt-closed', onClosed); if (ev.detail?.saved) window.location.reload() }
          parent.addEventListener('aj-gantt-closed', onClosed)
          parent.dispatchEvent(new parent.CustomEvent('open-gantt-modal', { detail: { projectId: pid, fromFrame: true } }))
          return
        }
      }
      savedRef.current = false
      setOnTop(!!e.detail?.fromFrame)
      setProjectId(pid)
      setOpen(true)
    }
    window.__ajGanttReady = true
    window.addEventListener('open-gantt-modal', onOpen)
    return () => { window.__ajGanttReady = false; window.removeEventListener('open-gantt-modal', onOpen) }
  }, [])

  const startHeartbeat = useCallback((pid) => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    heartbeatRef.current = setInterval(async () => {
      try {
        const r = await heartbeatLock('schedule', pid)
        if (!r.locked) { setReadOnly(true); setLockHolder(r.heldBy || null) }
      } catch { /* retry next tick */ }
    }, HEARTBEAT_MS)
  }, [])

  useEffect(() => {
    if (!open || !projectId) return
    let cancelled = false
    didCenterRef.current = false
    ;(async () => {
      setLoading(true); setError(null); setReadOnly(false); setLockHolder(null)
      try {
        const userName = ctx.currentMember?.fields?.['Name'] || ctx.currentMember?.fields?.['Full Name'] || ''
        let lock
        try { lock = await acquireLock('schedule', projectId, userName) }
        catch { lock = { locked: true } }
        if (cancelled) return
        if (lock && lock.locked === false) { setReadOnly(true); setLockHolder(lock.heldBy || null) }
        else startHeartbeat(projectId)

        // Fetch this project's milestones directly — NEVER filter ctx.state.milestones (2026-09-10
        // fix). That cache is scoped to whatever project the OUTLINE happens to be zoomed into
        // behind this modal, which has nothing to do with `projectId` here when this is opened
        // from the Flight Board's "Open schedule" button (a different project, or none zoomed at
        // all) — the filter silently returned [] and seedFromMilestones's own self-healing prune
        // (api/schedule.js's "seed" action: "anything not in that list for this project is gone")
        // then DELETED every existing schedule_milestones row for that project, matching Matt's
        // report exactly ("no milestones with dates" — it wasn't a display bug, the mirror was
        // wiped on open). api/schedule.js also gained a same-day guard refusing to prune on an
        // empty incoming list, as defense in depth against this exact failure mode recurring.
        const records = await getProjectMilestones(projectId)
        setMsRecords(records || [])
        setPhaseList([])
        getProjectPhases(projectId).then(ph => { if (!cancelled) setPhaseList(ph || []) }).catch(() => {})
        const { milestones, dependencies, effort: effortMap } = await seedFromMilestones(projectId, records)
        if (cancelled) return
        const sorted = [...(milestones || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
        setRows(sorted)
        setDeps(dependencies || [])
        setEffort(effortMap || {})
        setTimeline(null)
        getProjectTimeline(projectId).then(t => { if (!cancelled) setTimeline(t) })
        setFeeders({})
        getProjectFeeders(projectId).then(f => { if (!cancelled) setFeeders((f && f.byMilestone) || {}) }).catch(() => {})
        setKeyDates([])
        getProjectKeyDates(projectId).then(kds => { if (!cancelled) setKeyDates(kds || []) }).catch(() => {})
        loadBuildReady().then(d => {
          const p = d && (d.projects || []).find(x => x.projectId === projectId)
          if (cancelled || !p || p.reached || !p.target || !p.burn) { if (!cancelled) setBuildReady(null); return }
          setBuildReady({ target: p.target.date, projected: p.burn.projected, bufferDays: p.target.bufferDays, margin: p.burn.margin, late: !!p.burn.late, forecast: p.forecast || null })
          // A forecast is read-only: let go of the edit lock this window took, so nobody else is kept out.
          if (p.forecast) {
            if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null }
            releaseLock('schedule', projectId).catch(() => {})
            setReadOnly(true)
          }
        }).catch(() => {})
        const snap = new Map()
        sorted.forEach(r => snap.set(r.airtable_id, { est: r.earliest_start, lft: r.latest_finish, duration: r.duration_days, pinned: !!r.date_pinned }))
        snapshotRef.current = snap
        depsSnapRef.current = new Set((dependencies || []).map(d => d.milestone_id + '|' + d.predecessor_id))
      } catch (e) {
        if (!cancelled) setError(e.message || 'Failed to load schedule')
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [open, projectId, startHeartbeat])

  function changedRows() {
    return rows.filter(r => {
      const s = snapshotRef.current.get(r.airtable_id)
      if (!s) return false
      return s.est !== r.earliest_start || s.lft !== r.latest_finish || s.duration !== r.duration_days || s.pinned !== !!r.date_pinned
    })
  }

  // Dependency edits since open: the current edge set vs the snapshot.
  function depsDirty() {
    const now = new Set(deps.map(d => d.milestone_id + '|' + d.predecessor_id))
    const snap = depsSnapRef.current
    if (now.size !== snap.size) return true
    for (const k of now) if (!snap.has(k)) return true
    return false
  }
  // Predecessor-id list per dependent milestone (for the Airtable Predecessor write-back).
  function predecessorListsByMs() {
    const m = new Map()
    deps.forEach(d => { if (!m.has(d.milestone_id)) m.set(d.milestone_id, []); m.get(d.milestone_id).push(d.predecessor_id) })
    return m
  }

  // Cancel — discard unsaved edits (with a warning) and close. Used by the X and backdrop.
  async function cancel() {
    const dirty = readOnly ? 0 : changedRows().length
    if (dirty > 0 && !(await confirmDialog({ title: 'Discard unsaved changes?', message: 'Discard ' + dirty + ' unsaved change' + (dirty > 1 ? 's' : '') + '? Your edits will be lost.', confirmLabel: 'Discard changes', danger: true }))) return
    finishClose()
  }

  // Save — persist edits (dates, lock, dependencies) to Supabase + Airtable, then close.
  async function save() {
    const changes = readOnly ? [] : changedRows()
    const dDirty = !readOnly && depsDirty()
    if (!changes.length && !dDirty) { finishClose(); return }
    setSaving(true)
    try {
      if (changes.length) {
        const current = await fetchCurrentAirtableDates(changes.map(c => c.airtable_id))
        const drifted = changes.filter(c => {
          const s = snapshotRef.current.get(c.airtable_id), cur = current.get(c.airtable_id)
          if (!s || !cur) return false
          return cur.est !== s.est || cur.lft !== s.lft || cur.duration !== s.duration
        })
        if (drifted.length) {
          const proceed = await confirmDialog({
            title: 'Milestones changed in Airtable',
            message: 'These changed since you opened the scheduler (likely the nightly job):\n\n' +
              drifted.map(d => d.name).join(', ') +
              '\n\nOverwrite them with your edits? Cancel to discard your changes and reload.',
            confirmLabel: 'Overwrite with my edits',
          })
          if (!proceed) { finishClose(); return }
        }
        const payload = changes.map(c => ({ airtable_id: c.airtable_id, earliest_start: c.earliest_start, latest_finish: c.latest_finish, duration_days: c.duration_days, date_pinned: !!c.date_pinned }))
        await saveScheduleDates(payload)
        const { failed } = await writeBackDates(payload)
        const failedIds = new Set(failed.map(f => f.id))
        if (failedIds.size) {
          await saveScheduleDates(changes.filter(c => failedIds.has(c.airtable_id)).map(c => ({ airtable_id: c.airtable_id, pending_airtable_sync: true })))
        }
        changes.forEach(c => {
          if (failedIds.has(c.airtable_id)) return
          const ms = ctx.state?.milestones?.find(m => m.id === c.airtable_id)
          if (ms) { ms.fields[F_EST] = c.earliest_start; ms.fields[F_LFT] = c.latest_finish; ms.fields[F_DUR] = c.duration_days; ms.fields['Date Pinned'] = !!c.date_pinned }
        })
        if (failedIds.size) {
          window.dispatchEvent(new Event('outline-render'))
          setError(failed.length + ' milestone(s) failed to save to Airtable and are flagged for retry: ' +
            changes.filter(c => failedIds.has(c.airtable_id)).map(c => c.name).join(', '))
          setSaving(false); return
        }
      }
      if (dDirty) {
        // Milestones whose predecessor set changed → write their full Predecessor link to Airtable.
        const snapByMs = new Map()
        depsSnapRef.current.forEach(k => { const [ms, pred] = k.split('|'); if (!snapByMs.has(ms)) snapByMs.set(ms, new Set()); snapByMs.get(ms).add(pred) })
        const nowByMs = new Map()
        deps.forEach(d => { if (!nowByMs.has(d.milestone_id)) nowByMs.set(d.milestone_id, new Set()); nowByMs.get(d.milestone_id).add(d.predecessor_id) })
        const affected = [...new Set([...snapByMs.keys(), ...nowByMs.keys()])].filter(ms => {
          const a = snapByMs.get(ms) || new Set(), b = nowByMs.get(ms) || new Set()
          if (a.size !== b.size) return true
          for (const x of b) if (!a.has(x)) return true
          return false
        })
        const items = affected.map(ms => ({ airtable_id: ms, predecessor_ids: [...(nowByMs.get(ms) || new Set())] }))
        if (items.length) await writeBackPredecessors(items)
        await saveDependencies(projectId, deps)
        items.forEach(it => { const ms = ctx.state?.milestones?.find(m => m.id === it.airtable_id); if (ms) ms.fields['Predecessor'] = it.predecessor_ids })
      }
      window.dispatchEvent(new Event('outline-render'))
      savedRef.current = true
    } catch (e) {
      setError('Save failed: ' + (e.message || 'unknown error')); setSaving(false); return
    }
    finishClose()
  }

  function finishClose() {
    // Tells an embedded frame that handed this Gantt up (see onOpen) whether to reload.
    window.dispatchEvent(new CustomEvent('aj-gantt-closed', { detail: { saved: savedRef.current } }))
    savedRef.current = false
    if (heartbeatRef.current) { clearInterval(heartbeatRef.current); heartbeatRef.current = null }
    if (projectId && !readOnly) releaseLock('schedule', projectId).catch(() => {})
    setOpen(false); setProjectId(null); setRows([]); setDeps([]); setKeyDates([]); setError(null); setSaving(false)
    setSelectedId(null); setMenuFor(null); setLinking(null); setCreating(null)
    setPast([]); setFuture([])
  }

  useEffect(() => () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current) }, [])

  // ── Editing ─────────────────────────────────────────────────────────────────
  const cascadeInputs = useCallback((rowList) => ({
    milestones: rowList.map(r => ({ id: r.airtable_id, earliestStart: r.earliest_start, latestFinish: r.latest_finish, datePinned: r.date_pinned, behavior: r.milestone_behavior })),
    dependencies: deps.map(d => ({ milestoneId: d.milestone_id, predecessorId: d.predecessor_id })),
  }), [deps])

  function applyChange(rowId, nextEst, nextLft) {
    setRows(prev => {
      // A Fixed/Scheduled type (Public Hearing, Community Meeting, Field Report) flows with the schedule
      // until someone sets its real date by hand; setting it LOCKS it (Matt, 2026-09-24), so the date an
      // agency or client gave us can't drift when earlier work slips.
      const fixedType = (r) => r.milestone_behavior === 'Fixed' || r.milestone_behavior === 'Scheduled'
      let next = prev.map(r => r.airtable_id === rowId ? { ...r, earliest_start: nextEst, latest_finish: nextLft, ...(fixedType(r) ? { date_pinned: true } : {}) } : r)
      const { milestones, dependencies } = cascadeInputs(next)
      const updates = computeCascade({ rootId: rowId, rootFinish: nextLft, milestones, dependencies })
      if (updates.size) next = next.map(r => updates.has(r.airtable_id) ? { ...r, earliest_start: updates.get(r.airtable_id).earliestStart, latest_finish: updates.get(r.airtable_id).latestFinish } : r)
      return next
    })
  }

  function setDuration(rowId, value) {
    const n = Math.max(0, parseInt(value, 10) || 0)
    setRows(prev => prev.map(r => r.airtable_id === rowId ? { ...r, duration_days: n } : r))
  }

  // ── Undo / Redo ──────────────────────────────────────────────────────────────
  // Call recordSnapshot() BEFORE any mutating operation; it saves {rows, deps} to the undo stack
  // and clears the redo stack. undo()/redo() walk back and forward through the stack.
  function recordSnapshot() {
    if (readOnly) return
    const snap = { rows: [...rows], deps: [...deps] }
    setPast(p => [...p.slice(-49), snap])
    setFuture([])
  }
  function undo() {
    if (readOnly || past.length === 0) return
    const prev = past[past.length - 1]
    setFuture(f => [{ rows: [...rows], deps: [...deps] }, ...f.slice(0, 49)])
    setPast(p => p.slice(0, -1))
    setRows(prev.rows)
    setDeps(prev.deps)
  }
  function redo() {
    if (readOnly || future.length === 0) return
    const next = future[0]
    setPast(p => [...p.slice(-49), { rows: [...rows], deps: [...deps] }])
    setFuture(f => f.slice(1))
    setRows(next.rows)
    setDeps(next.deps)
  }

  function togglePhase(pid) {
    setCollapsedPhases(prev => { const n = new Set(prev); n.has(pid) ? n.delete(pid) : n.add(pid); return n })
  }

  // ── Lock + dependency editing (Instagantt-style wrench menu + connector grip) ──
  function toggleLock(rowId) {
    recordSnapshot()
    setRows(prev => prev.map(r => r.airtable_id === rowId ? { ...r, date_pinned: !r.date_pinned } : r))
    setMenuFor(null)
  }
  function removeDep(msId, predId) {
    recordSnapshot()
    setDeps(prev => prev.filter(d => !(d.milestone_id === msId && d.predecessor_id === predId)))
  }
  // Unlinking a predecessor that hasn't posted is usually a wish to START EARLY, not a wrong link
  // (Santiago's DAPC, 2026-10-22: the link was deleted to begin drawing, and the schedule then waited on
  // nothing). Ask which it is: start ahead keeps the link (docs/COMPRESSED_CHAIN.md); a wrong link goes.
  function unlinkOrStartAhead(msId, predId) {
    const pred = rows.find(x => x.airtable_id === predId)
    if (!pred || postedDateOf(pred)) { removeDep(msId, predId); return }
    window.dispatchEvent(new CustomEvent('open-start-ahead', { detail: {
      msId, predId, fromGantt: true,
      msName: rows.find(x => x.airtable_id === msId)?.name, predName: pred.name,
      onUnlink: () => removeDep(msId, predId),
    } }))
  }
  // Clicking the × on a connector line — confirm first so a stray click can't silently kill a link.
  async function confirmRemoveDep(d) {
    if (readOnly) return
    const predName = rows.find(x => x.airtable_id === d.predecessor_id)?.name || 'the predecessor'
    const msName = rows.find(x => x.airtable_id === d.milestone_id)?.name || 'this milestone'
    const predRow = rows.find(x => x.airtable_id === d.predecessor_id)
    if (predRow && !postedDateOf(predRow)) { unlinkOrStartAhead(d.milestone_id, d.predecessor_id); return }
    const ok = await confirmDialog({
      title: 'Remove dependency?',
      message: '"' + msName + '" will no longer depend on "' + predName + '" finishing first.',
      confirmLabel: 'Remove link',
      danger: true,
    })
    if (ok) removeDep(d.milestone_id, d.predecessor_id)
  }
  // Create "predId is a predecessor of msId"; guards self/dup/direct-reverse, then reflows the dependent.
  function addDep(predId, msId) {
    if (!predId || !msId || predId === msId) return
    if (deps.some(d => d.milestone_id === msId && d.predecessor_id === predId)) return        // already linked
    if (deps.some(d => d.milestone_id === predId && d.predecessor_id === msId)) return         // direct cycle
    recordSnapshot()
    const nextDeps = [...deps, { milestone_id: msId, predecessor_id: predId, lag_days: 1 }]
    setDeps(nextDeps)
    const pred = rows.find(r => r.airtable_id === predId)
    if (pred && pred.latest_finish) {
      setRows(prev => {
        const mils = prev.map(r => ({ id: r.airtable_id, earliestStart: r.earliest_start, latestFinish: r.latest_finish, datePinned: r.date_pinned, behavior: r.milestone_behavior }))
        const depList = nextDeps.map(d => ({ milestoneId: d.milestone_id, predecessorId: d.predecessor_id }))
        const updates = computeCascade({ rootId: predId, rootFinish: pred.latest_finish, milestones: mils, dependencies: depList })
        if (!updates.size) return prev
        return prev.map(r => updates.has(r.airtable_id) ? { ...r, earliest_start: updates.get(r.airtable_id).earliestStart, latest_finish: updates.get(r.airtable_id).latestFinish } : r)
      })
    }
  }

  // Connector grip drag — starts at the bar's START (left ●), drag to a predecessor → THIS becomes the dependent.
  const linkRef = useRef(null)
  function onGripDown(e, row) {
    if (readOnly) return
    e.preventDefault(); e.stopPropagation()
    const rect = contentRef.current?.getBoundingClientRect()
    const pos = msPos.get(row.airtable_id)
    linkRef.current = { fromId: row.airtable_id, rect }
    if (pos && rect) setLinking({ fromId: row.airtable_id, x1: pos.left, y1: pos.mid, x2: e.clientX - rect.left, y2: e.clientY - rect.top })
    window.addEventListener('pointermove', onLinkMove)
    window.addEventListener('pointerup', onLinkUp)
  }
  function onLinkMove(e) {
    const l = linkRef.current; if (!l || !l.rect) return
    setLinking(prev => prev ? { ...prev, x2: e.clientX - l.rect.left, y2: e.clientY - l.rect.top } : prev)
  }
  function onLinkUp(e) {
    const l = linkRef.current
    window.removeEventListener('pointermove', onLinkMove)
    window.removeEventListener('pointerup', onLinkUp)
    linkRef.current = null
    setLinking(null)
    if (!l || !l.rect) return
    const localY = e.clientY - l.rect.top
    let target = null
    for (const node of layout) { if (node.kind === 'ms' && localY >= node.top && localY < node.top + ROW_H) { target = node.row.airtable_id; break } }
    // target becomes the predecessor of this (fromId) milestone
    if (target && target !== l.fromId) addDep(target, l.fromId)
  }

  // ── Drag ───────────────────────────────────────────────────────────────────
  function onBarPointerDown(e, row, mode) {
    setSelectedId(row.airtable_id)
    if (readOnly || isDragLocked(row)) return
    e.preventDefault(); e.stopPropagation()
    recordSnapshot()   // capture state before any drag movement
    dragRef.current = { rowId: row.airtable_id, mode, startX: e.clientX, est0: row.earliest_start, lft0: row.latest_finish }
    setDragTip({ x: e.clientX, y: e.clientY, est: row.earliest_start, lft: row.latest_finish, mode })
    window.addEventListener('pointermove', onDragMove)
    window.addEventListener('pointerup', onDragUp)
  }
  function onDragMove(e) {
    const d = dragRef.current; if (!d) return
    const deltaDays = Math.round((e.clientX - d.startX) / PX)
    const est0 = parseDate(d.est0), lft0 = parseDate(d.lft0); if (!lft0) return
    let nextEst = d.est0, nextLft = d.lft0
    if (d.mode === 'move' && est0) { nextEst = formatDate(addDays(est0, deltaDays)); nextLft = formatDate(addDays(lft0, deltaDays)) }
    else if (d.mode === 'start' && est0) { const c = addDays(est0, deltaDays); if (dayDiff(c, lft0) >= 0) nextEst = formatDate(c) }
    else if (d.mode === 'end') { const c = addDays(lft0, deltaDays); if (!est0 || dayDiff(est0, c) >= 0) nextLft = formatDate(c) }
    setDragTip({ x: e.clientX, y: e.clientY, est: nextEst, lft: nextLft, mode: d.mode })
    applyChange(d.rowId, nextEst, nextLft)
  }
  function onDragUp() {
    dragRef.current = null
    setDragTip(null)
    window.removeEventListener('pointermove', onDragMove)
    window.removeEventListener('pointerup', onDragUp)
  }

  // ── Draw-to-create (undated rows) ──────────────────────────────────────────
  // Pointer-down on the empty timeline row of an undated milestone: drag left→right to paint EST→LFT.
  function onCreatePointerDown(e, row) {
    if (readOnly || isDragLocked(row)) return
    e.preventDefault(); e.stopPropagation()
    recordSnapshot()
    const rect = contentRef.current?.getBoundingClientRect()
    if (!rect || !windowStart) return
    const x = e.clientX - rect.left
    createRef.current = { rowId: row.airtable_id, rect, x0: x, windowStart }
    setCreating({ rowId: row.airtable_id, x1: x, x2: x })
    window.addEventListener('pointermove', onCreateMove)
    window.addEventListener('pointerup', onCreateUp)
  }
  function onCreateMove(e) {
    const c = createRef.current; if (!c) return
    const x = e.clientX - c.rect.left
    setCreating(prev => prev ? { ...prev, x2: x } : prev)
  }
  function onCreateUp(e) {
    const c = createRef.current
    window.removeEventListener('pointermove', onCreateMove)
    window.removeEventListener('pointerup', onCreateUp)
    createRef.current = null
    setCreating(null)
    if (!c || !c.windowStart) return
    const rawX = e.clientX - c.rect.left
    const x1 = Math.min(c.x0, rawX)
    const x2 = Math.max(c.x0, rawX)
    const est = formatDate(addDays(c.windowStart, Math.max(0, Math.floor(x1 / PX))))
    const lft = formatDate(addDays(c.windowStart, Math.max(0, Math.floor(x2 / PX))))
    const dur  = Math.max(1, dayDiff(parseDate(est), parseDate(lft)))
    // Cascade into successors — including undated ones, which standard computeCascade skips.
    // BFS: dated successors preserve window width; undated successors use duration_days to place.
    const currentDeps = deps   // capture from closure before setRows
    setRows(prev => {
      const rowMap = new Map(prev.map(r => [r.airtable_id, r]))
      rowMap.set(c.rowId, { ...rowMap.get(c.rowId), earliest_start: est, latest_finish: lft, duration_days: dur })
      const dependentsMap = new Map()
      currentDeps.forEach(d => {
        if (!dependentsMap.has(d.predecessor_id)) dependentsMap.set(d.predecessor_id, [])
        dependentsMap.get(d.predecessor_id).push(d.milestone_id)
      })
      const visited = new Set()
      const queue = [{ id: c.rowId, finish: parseDate(lft) }]
      while (queue.length) {
        const { id: curId, finish: upFinish } = queue.shift()
        if (visited.has(curId)) continue
        visited.add(curId)
        if (!upFinish) continue
        for (const succId of (dependentsMap.get(curId) || [])) {
          const succ = rowMap.get(succId)
          if (!succ) continue
          if (succ.date_pinned) continue   // only a real, locked date holds (Fixed/Scheduled flow until set, 2026-09-24)
          const newEst = nextWorkday(addDays(upFinish, 1))
          const curEst = parseDate(succ.earliest_start), curLft = parseDate(succ.latest_finish)
          let newLft
          if (curLft && curEst) {
            newLft = addDays(newEst, Math.max(dayDiff(curEst, curLft), 0))
          } else {
            newLft = addDays(newEst, Math.max(succ.duration_days || 1, 1))
          }
          rowMap.set(succId, { ...succ, earliest_start: formatDate(newEst), latest_finish: formatDate(newLft) })
          queue.push({ id: succId, finish: newLft })
        }
      }
      return prev.map(r => rowMap.get(r.airtable_id) || r)
    })
  }

  // ── Geometry ────────────────────────────────────────────────────────────────
  // Phase grouping (ordered by this project's own phases, then any leftovers)
  // "Custom Title" (e.g. "Phase 2 Concept") is what actually distinguishes multiple Additional
  // Services sub-phases on the same project — "Phase Name" is the same generic "Additional
  // Services" string on every one of them, so preferring it made them all read identically.
  const phaseName = (pid) => {
    const p = phaseList.find(x => x.id === pid)
    return pid === '__none__' ? 'Unassigned' : (p?.fields?.['Custom Title'] || p?.fields?.['Phase Name'] || p?.fields?.['Name'] || 'Phase')
  }
  // Phase-name → id, so a milestone with no Phase LINK can still group by its Project Phase text.
  const phaseIdByName = new Map()
  phaseList.forEach(p => { const nm = p.fields?.['Phase Name'] || p.fields?.['Name']; if (nm) phaseIdByName.set(String(nm), p.id) })
  // Resolve a row's phase: the Phase link if present + known, else match the Project Phase text by name.
  // (Milestones carry both a Phase link and a Project Phase single-select; some have only one.)
  const phaseIdFor = (r) => {
    if (r.phase_id && phaseList.some(p => p.id === r.phase_id)) return r.phase_id
    const txt = infoMap.get(r.airtable_id)?.projectPhase
    if (txt && phaseIdByName.has(txt)) return phaseIdByName.get(txt)
    return r.phase_id || '__none__'
  }

  // Normalized rows for the shared Gantt library/component (gantt/GanttTimeline.jsx,
  // lib/ganttLayout.js) — `...r` keeps every original field (airtable_id, phase_id, etc.) so every
  // existing handler below (onBarPointerDown, setDuration, …) is unaffected; `id` is the alias the
  // shared code reads. A FEEDER (work owed to us — build step 5, docs/OPERATING_MODEL.md) draws as a
  // wait bar, with where its dates come from: their date, our estimate, our typical time, or late.
  // THE FORECAST (Matt, 2026-10-27): with a Build Ready plan, the bars are the forecast from postings
  // (api/_buildReady.js `forecast`): posted work on its real dates, open work cascading at its expected
  // length from today, the buffer leading to Build Ready, construction after it. Read-only: nobody drags a
  // forecast; dates the world sets (hearings) are pinned at the source.
  const forecast = buildReady && buildReady.forecast ? buildReady.forecast : null
  const timelineRows = useMemo(() => rows.map(r => {
    const fcast = forecast && forecast[r.airtable_id]
    if (fcast) r = { ...r, earliest_start: fcast.start, latest_finish: fcast.finish, fc: fcast }
    const e = effort[r.deliverable_type_airtable_id]
    const typical = e && e.effort_days_resolved != null ? Number(e.effort_days_resolved) : null
    const fd = feeders[r.airtable_id] || null
    return {
      ...r,
      id: r.airtable_id,
      posted: postedDateOf(r),
      progress: infoMap.get(r.airtable_id)?.progress || 0,
      assignee: infoMap.get(r.airtable_id)?.assignee || null,
      isWait: !!fd,
      feeder: fd,
      typicalDays: fd ? null : ((typical != null && typical !== 0) ? typical : null),
      // TOUCH TIME — the days of real work, from the curated estimate for its type (2026-09-24). The
      // dark fill, float and the "shorter than its work" red all read this, never duration_days
      // (which is the gap between the dates; the overnight scheduler keeps it so). null = no estimate.
      touchDays: typical,
      typicalLabel: fd ? null : (e ? ('Typical for "' + (e.name || 'this type') + '"' + (e.effort_source === 'curated' ? ' (curated firm estimate)' : ' (no curated estimate yet)')) : null),
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [rows, infoMap, effort, feeders, forecast])

  const { windowStart, timelineW, xOf, anchorX: todayX } = buildWindow(timelineRows, PX, todayUTC())
  const { layout, msPos, bodyH, lineNumMap } = buildPhaseLayout({
    rows: timelineRows, phaseKeyFor: phaseIdFor, phaseLabelFor: phaseName,
    orderedKeys: phaseList.map(p => p.id), collapsedKeys: collapsedPhases, rowH: ROW_H, xOf, px: PX,
  })

  function jumpToday() {
    if (todayX == null || !scrollRef.current) return
    scrollRef.current.scrollLeft = Math.max(0, todayX - (scrollRef.current.clientWidth - LABEL_W) / 2)
  }

  // Center on today once after first load
  useEffect(() => {
    if (!loading && rows.length && !didCenterRef.current && scrollRef.current) {
      didCenterRef.current = true
      requestAnimationFrame(jumpToday)
    }
  }) // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard nudge — ←/→ moves the selected bar's window ±1 day (cascade-aware), like Instagantt.
  useEffect(() => {
    if (!open) return
    function onKey(e) {
      // Undo / redo
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key === 'z') { e.preventDefault(); undo(); return }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'z'))) { e.preventDefault(); redo(); return }
      // Keyboard nudge
      if (readOnly || !selectedId || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return
      const r = rows.find(x => x.airtable_id === selectedId)
      if (!r || isDragLocked(r)) return
      const est = parseDate(r.earliest_start), lft = parseDate(r.latest_finish)
      if (!lft) return
      e.preventDefault()
      recordSnapshot()
      const delta = e.key === 'ArrowLeft' ? -1 : 1
      applyChange(selectedId, est ? formatDate(addDays(est, delta)) : r.earliest_start, formatDate(addDays(lft, delta)))
    }
    window.addEventListener('keydown', onKey)

    // Block scroll from reaching the page behind the modal.
    // Must be non-passive so preventDefault() actually works.
    // Only block events that originate outside the modal panel itself —
    // events from inside (the scroll area) must pass through untouched.
    const el = backdropRef.current
    const stopWheel = e => {
      if (!e.target.closest('[data-modal-id="GanttModal"]')) e.preventDefault()
    }
    if (el) el.addEventListener('wheel', stopWheel, { passive: false })

    return () => {
      window.removeEventListener('keydown', onKey)
      if (el) el.removeEventListener('wheel', stopWheel)
    }
  }) // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null
  const changeCount = readOnly ? 0 : changedRows().length
  const depsAreDirty = !readOnly && depsDirty()
  const anyDirty = changeCount > 0 || depsAreDirty

  return createPortal(
    <div
      ref={backdropRef}
      style={{ position: 'fixed', inset: 0, zIndex: onTop ? 1000002 : 700, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onTouchMove={e => { if (!e.target.closest('[data-modal-id="GanttModal"]')) e.preventDefault() }}
    >
      <div data-modal-id="GanttModal" style={{ background: BG, borderRadius: 10, boxShadow: '0 8px 40px rgba(0,0,0,0.22)', width: '96vw', height: '93vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 18px', borderBottom: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)' }}>
          <span style={{ fontFamily: 'Montserrat,sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.06em', textTransform: 'uppercase', color: TEXT }}>Schedule</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            {/* Zoom segmented control */}
            <div style={{ display: 'flex', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, overflow: 'hidden' }}>
              {Object.entries(ZOOMS).map(([k, z]) => (
                <button key={k} onClick={() => setZoom(k)}
                  style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '4px 10px', border: 'none', cursor: 'pointer',
                    background: zoom === k ? PRIMARY : 'var(--aj-bg-elevated)', color: zoom === k ? '#fff' : 'var(--aj-text-mid)' }}>{z.label}</button>
              ))}
            </div>
            {/* Undo / Redo */}
            {!readOnly && (
              <div style={{ display: 'flex', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, overflow: 'hidden' }}>
                <button onClick={undo} disabled={past.length === 0} title="Undo (Ctrl+Z)"
                  style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '3px 10px', border: 'none', cursor: past.length === 0 ? 'default' : 'pointer', background: 'var(--aj-bg-elevated)', color: TEXT, opacity: past.length === 0 ? 0.3 : 1 }}>↺</button>
                <button onClick={redo} disabled={future.length === 0} title="Redo (Ctrl+Y)"
                  style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '3px 10px', border: 'none', borderLeft: '0.5px solid var(--aj-border)', cursor: future.length === 0 ? 'default' : 'pointer', background: 'var(--aj-bg-elevated)', color: TEXT, opacity: future.length === 0 ? 0.3 : 1 }}>↻</button>
              </div>
            )}
            <button onClick={jumpToday} disabled={todayX == null}
              style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, padding: '4px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: TEXT, cursor: todayX == null ? 'default' : 'pointer', opacity: todayX == null ? 0.5 : 1 }}>Today</button>
            {/* The Build Ready countdown replaces the old chain/buffer chip (docs/ALDERFLOW.md §5c2). */}
            <BuildReadyStrip projectId={projectId} compact />
            {!readOnly && <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: anyDirty ? '#1a6fa8' : 'var(--aj-text-mid)' }}>{changeCount} unsaved{depsAreDirty ? ' · deps' : ''}</span>}
            <button onClick={() => !saving && cancel()} disabled={saving} title="Cancel — discard unsaved changes" style={{ background: 'none', border: 'none', cursor: saving ? 'default' : 'pointer', fontSize: 20, color: 'var(--aj-text-mid)', lineHeight: 1 }}>×</button>
          </div>
        </div>

        {forecast
          ? <div style={{ padding: '7px 18px', background: 'rgba(29,158,117,0.08)', borderBottom: '0.5px solid rgba(29,158,117,0.35)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#0f6b48' }}>The forecast from postings: posted work on its real dates, open work at the time we expect it to take, the buffer up to Build Ready, construction after it. It moves only as work posts.</div>
          : readOnly && <div style={{ padding: '7px 18px', background: 'rgba(224,160,32,0.12)', borderBottom: '0.5px solid rgba(224,160,32,0.4)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#b7651a' }}>{lockHolder?.name ? lockHolder.name + ' is editing this schedule — read-only.' : 'This schedule is locked by another user — read-only.'}</div>}
        {error && <div style={{ padding: '7px 18px', background: 'rgba(192,57,43,0.10)', borderBottom: '0.5px solid rgba(192,57,43,0.35)', fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#c0392b' }}>{error}</div>}

        {/* Body */}
        <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {loading && <div style={{ padding: 30, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' }}>Loading schedule…</div>}
          {!loading && rows.length === 0 && <div style={{ padding: 30, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text-mid)' }}>No milestones with dates for this project.</div>}

          {!loading && rows.length > 0 && (
            <div style={{ display: 'flex', minWidth: LABEL_W + timelineW }}>
              {/* Left column */}
              <div style={{ width: LABEL_W, flexShrink: 0, position: 'sticky', left: 0, zIndex: 3, background: BG, borderRight: '0.5px solid var(--aj-border)' }}>
                <div style={{ height: headH, borderBottom: '0.5px solid var(--aj-border)', display: 'flex', alignItems: 'center', padding: '0 8px', fontFamily: 'Montserrat,sans-serif', fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--aj-text-mid)', position: 'sticky', top: 0, zIndex: 4, background: BG }}>
                  <span style={{ width: 22, textAlign: 'right', flexShrink: 0, paddingRight: 6 }}>#</span>
                  <span style={{ flex: 1 }}>Milestone</span>
                  <span title="Depends on: the row numbers of the milestones this one waits for" style={{ width: 48, textAlign: 'right', flexShrink: 0, cursor: 'help' }}>DPD</span>
                  <span title={forecast ? 'Days of work: the time the work itself takes. The bar is usually longer, because it is planned at our usual pace (hover a bar for its numbers).' : 'Days of work: the time the work itself takes (the dark part of the bar).'} style={{ width: 44, textAlign: 'right', flexShrink: 0, cursor: 'help' }}>Days</span>
                </div>
                {layout.map((node, i) => node.kind === 'phase' ? (
                  <div key={'p' + node.pid + i} onClick={() => togglePhase(node.pid)}
                    style={{ height: ROW_H, display: 'flex', alignItems: 'center', gap: 0, padding: '0 8px', cursor: 'pointer', background: 'var(--aj-bg-muted)', borderBottom: '0.5px solid var(--aj-border)' }}>
                    <span style={{ fontSize: 10, color: 'var(--aj-text-mid)', width: 22, flexShrink: 0, textAlign: 'center' }}>{node.collapsed ? '▶' : '▼'}</span>
                    <span style={{ flex: 1, fontFamily: 'Montserrat,sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{node.name}</span>
                    <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-mid)', width: 48, textAlign: 'right', flexShrink: 0 }}>{node.count}</span>
                    <span style={{ width: 44, flexShrink: 0 }} />
                  </div>
                ) : (() => {
                  const dpdNums = deps
                    .filter(d => d.milestone_id === node.row.airtable_id)
                    .map(d => lineNumMap.get(d.predecessor_id))
                    .filter(Boolean)
                    .join(',')
                  return (
                    <div key={node.row.airtable_id}
                      onMouseEnter={() => setHoverRowId(node.row.airtable_id)}
                      onMouseLeave={() => setHoverRowId(h => h === node.row.airtable_id ? null : h)}
                      style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 8px', gap: 0, borderBottom: '0.5px solid var(--aj-border)', background: hoverRowId === node.row.airtable_id ? ROW_HILITE : 'transparent' }}>
                      <span style={{ width: 22, flexShrink: 0, textAlign: 'right', paddingRight: 6, fontFamily: '"DM Mono",monospace', fontSize: 10, color: 'var(--aj-text-muted)' }}>{node.lineNum}</span>
                      <span title={postedDateOf(node.row) ? node.row.name + ' — posted ' + fmtShort(postedDateOf(node.row)) : node.row.name} style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: postedDateOf(node.row) ? 'var(--aj-text-muted)' : TEXT, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
                        {postedDateOf(node.row) && <span title={'Posted ' + fmtShort(postedDateOf(node.row))} style={{ marginRight: 5, color: PROG, fontWeight: 700 }}>✓</span>}
                        {isLocked(node.row) && <span title="Pinned / fixed" style={{ marginRight: 4, opacity: 0.5 }}>🔒</span>}{node.row.name}
                      </span>
                      <span title={dpdNums ? 'Depends on: ' + dpdNums : ''} style={{ width: 48, flexShrink: 0, textAlign: 'right', paddingRight: 4, fontFamily: '"DM Mono",monospace', fontSize: 10, color: dpdNums ? 'var(--aj-text-mid)' : 'transparent' }}>{dpdNums || '·'}</span>
                      {(() => {
                        // Touch time, read-only: the firm's estimate for this milestone type (changed in
                        // Templates). A feeder shows "wait" — the whole bar is time owed by someone else.
                        const tl = timelineRows.find(x => x.id === node.row.airtable_id) || {}
                        const t = tl.touchDays
                        const label = tl.isWait ? 'wait' : (t == null ? '—' : (Math.round(t * 10) / 10) + 'd')
                        const title = tl.isWait ? 'Time owed by someone else' : (t == null ? 'No touch-time estimate for this milestone type yet' : 'Touch time: the firm estimate of the real work for this milestone type')
                        return <span title={title} style={{ width: 44, flexShrink: 0, fontFamily: '"DM Mono",monospace', fontSize: 11, padding: '2px 4px', textAlign: 'right', color: tl.isWait ? 'var(--aj-text-muted)' : TEXT }}>{label}</span>
                      })()}
                    </div>
                  )
                })())}
                {buildReady && (
                  <div title="The project buffer before Build Ready: it shrinks as the work runs past the plan" style={{ height: ROW_H, display: 'flex', alignItems: 'center', padding: '0 8px', borderBottom: '0.5px solid var(--aj-border)', background: 'rgba(29,158,117,0.05)' }}>
                    <span style={{ width: 22, flexShrink: 0 }} />
                    <span style={{ flex: 1, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, fontWeight: 600, color: '#1D9E75' }}>Buffer to Build Ready</span>
                  </div>
                )}
              </div>

              {/* Timeline — the shared Gantt chart component; see gantt/GanttTimeline.jsx */}
              <GanttTimeline
                ref={contentRef}
                rows={timelineRows} deps={deps} keyDates={keyDates} buildReady={buildReady}
                phaseKeyFor={phaseIdFor} phaseLabelFor={phaseName}
                orderedPhaseKeys={phaseList.map(p => p.id)} collapsedKeys={collapsedPhases}
                zoom={zoom} rowH={ROW_H} headH={headH}
                hoveredRowId={hoverRowId} onHoverRow={setHoverRowId}
                selectedId={selectedId} readOnly={readOnly}
                interactive={{
                  onBarPointerDown, onGripPointerDown: onGripDown,
                  onWrenchClick: (e, row) => setMenuFor({ id: row.airtable_id, x: e.clientX, y: e.clientY }),
                  onDepClick: confirmRemoveDep,
                  onCreatePointerDown, isDragLocked,
                  dragTip, linking, creating,
                }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 18px', borderTop: '0.5px solid var(--aj-border)', background: 'var(--aj-bg-elevated)' }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-mid)' }}>{forecast ? 'Days = days of work · each bar = those days at our usual pace · hover a bar for its numbers · ' : 'Drag to move · Days = days of work (dark fill) · ←/→ to nudge · Ctrl+Z / Ctrl+Y to undo/redo · '}<span style={{ color: CRIT }}>red outline = critical path</span> · <span style={{ color: '#8e1c1c' }}>deep red = shorter than its work</span> · <span style={{ color: '#c9821a' }}>amber = feeder</span> · <span style={{ color: PROG }}>green = % complete</span> · <span style={{ color: '#8a5cc2' }}>◆ = key date</span></span>
          {readOnly ? (
            <button onClick={() => !saving && finishClose()} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 4, border: 'none', background: PRIMARY, color: '#fff', cursor: 'pointer' }}>Close</button>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => !saving && cancel()} disabled={saving} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, padding: '6px 14px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', background: 'transparent', color: 'var(--aj-text-mid)', cursor: saving ? 'default' : 'pointer' }}>Cancel</button>
              <button onClick={() => !saving && save()} disabled={saving || !anyDirty} style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '6px 16px', borderRadius: 4, border: 'none', background: PRIMARY, color: '#fff', cursor: (saving || !anyDirty) ? 'default' : 'pointer', opacity: (saving || !anyDirty) ? 0.5 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          )}
        </div>
      </div>

      {/* Action (wrench) menu — lock dates + unlink predecessors */}
      {menuFor && (() => {
        const r = rows.find(x => x.airtable_id === menuFor.id)
        if (!r) return null
        const preds = deps.filter(d => d.milestone_id === menuFor.id)
        const nameOf = (id) => rows.find(x => x.airtable_id === id)?.name || 'milestone'
        const left = Math.min(menuFor.x, window.innerWidth - 240)
        const top = Math.min(menuFor.y, window.innerHeight - 220)
        const item = { display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: TEXT, padding: '7px 9px', borderRadius: 5 }
        return (
          <>
            <div onClick={() => setMenuFor(null)} style={{ position: 'fixed', inset: 0, zIndex: 730 }} />
            <div style={{ position: 'fixed', left, top, zIndex: 731, background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 8, boxShadow: '0 6px 28px rgba(0,0,0,0.22)', padding: 5, width: 226, fontFamily: '"DM Sans",sans-serif' }}>
              <div style={{ fontSize: 10.5, color: 'var(--aj-text-mid)', padding: '4px 9px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</div>
              <button onClick={() => toggleLock(menuFor.id)} onMouseEnter={e => e.currentTarget.style.background = HOVER_BG} onMouseLeave={e => e.currentTarget.style.background = 'none'} style={item}>{r.date_pinned ? '🔓  Unlock dates' : '🔒  Lock dates'}</button>
              <div style={{ borderTop: '0.5px solid var(--aj-border)', margin: '4px 0' }} />
              {preds.length === 0
                ? <div style={{ fontSize: 11, color: 'var(--aj-text-mid)', padding: '4px 9px', lineHeight: 1.4 }}>No predecessors. Drag the ● grip on the left of a bar to a predecessor to link one.</div>
                : preds.map(d => (
                    <button key={d.predecessor_id} onClick={() => { unlinkOrStartAhead(menuFor.id, d.predecessor_id); setMenuFor(null) }} onMouseEnter={e => e.currentTarget.style.background = HOVER_BG} onMouseLeave={e => e.currentTarget.style.background = 'none'} style={item}>
                      <span style={{ color: CRIT, marginRight: 4 }}>✕</span> Unlink <span style={{ color: 'var(--aj-text-mid)' }}>{nameOf(d.predecessor_id)}</span>
                    </button>
                  ))}
            </div>
          </>
        )
      })()}
    </div>,
    document.body
  )
}
